import { useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import Icon from './Icon';
import { shortDay } from '../when';

/**
 * FLIGHTDECK'S OWN CALENDAR — the popover the date field's button opens, in the
 * app's theme rather than the browser's grey native picker.
 *
 * Deliberately spare: one quiet month — a title, prev/next arrows, the grid
 * (today ringed, the chosen day filled) and a Today/Clear footer. It is only
 * for POINTING at a nearby day. Everything else people reach for — the relative
 * words (today, fri, +2w, eom) and jumping months or years ahead — the typed
 * field already does in a keystroke and shows the day it resolved to, so the
 * calendar sheds the chip row and the month/year drop-downs it used to carry.
 * Picking a day hands a plain `YYYY-MM-DD` back and closes.
 */
const MONTHS_FULL = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const DOW = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

function iso(y: number, m: number, d: number): string {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

export default function CalendarPopover({
  value,
  onPick,
  onClear,
  onClose,
  anchorRef,
  min,
  max,
  todayMs = Date.now(),
}: {
  value: string;                 // selected day YYYY-MM-DD, or ''
  onPick: (iso: string) => void;
  onClear: () => void;
  onClose: () => void;
  anchorRef: RefObject<HTMLElement | null>;
  min?: string;                  // earliest allowed day — earlier days are dimmed
  max?: string;                  // latest allowed day — later days are dimmed
  todayMs?: number;
}) {
  const now = new Date(todayMs);
  const todayIso = iso(now.getFullYear(), now.getMonth(), now.getDate());
  const start = value && /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? { y: Number(value.slice(0, 4)), m: Number(value.slice(5, 7)) - 1 }
    : { y: now.getFullYear(), m: now.getMonth() };
  const [y, setY] = useState(start.y);
  const [m, setM] = useState(start.m);

  // Six weeks of cells, Monday-first, prev/next spill muted.
  const cells = useMemo(() => {
    const lead = (new Date(y, m, 1).getDay() + 6) % 7;   // Mon=0 … Sun=6
    const out: { d: number; iso: string; mut: boolean }[] = [];
    for (let i = 0; i < 42; i++) {
      const dt = new Date(y, m, 1 - lead + i);
      out.push({ d: dt.getDate(), iso: iso(dt.getFullYear(), dt.getMonth(), dt.getDate()), mut: dt.getMonth() !== m });
    }
    return out;
  }, [y, m]);

  function step(delta: number) {
    const dt = new Date(y, m + delta, 1);
    setY(dt.getFullYear());
    setM(dt.getMonth());
  }

  // Rendered in a portal at <body> so the scrollable card body can't clip it,
  // and positioned by hand off the calendar button: below when it fits, flipped
  // above when it does not, always clamped inside the window.
  const pop = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    function place() {
      const a = anchorRef.current?.getBoundingClientRect();
      const el = pop.current;
      if (!a || !el) return;
      const gap = 6, pad = 8;
      const w = el.offsetWidth, h = el.offsetHeight;
      let left = a.right - w;                                  // right edge under the button
      left = Math.min(Math.max(pad, left), window.innerWidth - w - pad);
      const below = a.bottom + gap;
      const above = a.top - gap - h;
      // Below by default; flip above when it would run off the bottom and there
      // is more room up top; then clamp so it never leaves the window.
      let top = below;
      if (below + h > window.innerHeight - pad && (above >= pad || a.top > window.innerHeight - a.bottom)) top = above;
      top = Math.min(Math.max(pad, top), window.innerHeight - h - pad);
      setPos({ top, left });
    }
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [anchorRef, y, m]);

  // Close on a click outside both the popover and its button, and on Escape —
  // the app-wide popover rule, here at document level because the portal sits
  // outside the field's DOM.
  useEffect(() => {
    function onDown(e: MouseEvent) {
      const t = e.target as Node;
      if (pop.current?.contains(t) || anchorRef.current?.contains(t)) return;
      onClose();
    }
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } }
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [anchorRef, onClose]);

  return createPortal(
    <div
      ref={pop}
      className="cal-pop"
      role="dialog"
      aria-label="Choose a date"
      style={{ top: pos?.top ?? -9999, left: pos?.left ?? -9999, visibility: pos ? 'visible' : 'hidden' }}
    >
      <div className="cal-head">
        <span className="cal-title">{MONTHS_FULL[m]} {y}</span>
        <span className="cal-nav">
          <button type="button" aria-label="Previous month" onClick={() => step(-1)}><Icon name="chevronLeft" size={16} /></button>
          <button type="button" aria-label="Next month" onClick={() => step(1)}><Icon name="chevron" size={16} /></button>
        </span>
      </div>

      <div className="cal-dow">{DOW.map((d) => <span key={d}>{d}</span>)}</div>
      <div className="cal-grid">
        {cells.map((c, i) => {
          const off = (!!min && c.iso < min) || (!!max && c.iso > max);   // out of range — not pickable
          const cls = ['cal-day'];
          if (c.mut) cls.push('mut');
          if (c.iso === todayIso) cls.push('today');
          if (c.iso === value) cls.push('sel');
          if (off) cls.push('off');
          return (
            <button
              key={i}
              type="button"
              className={cls.join(' ')}
              aria-label={shortDay(c.iso)}
              aria-current={c.iso === todayIso ? 'date' : undefined}
              disabled={off}
              onClick={() => onPick(c.iso)}
            >{c.d}</button>
          );
        })}
      </div>

      <div className="cal-foot">
        <button type="button" className="cal-link" onClick={() => onPick(todayIso)}>Today</button>
        <button type="button" className="cal-link cal-muted" onClick={onClear}>Clear</button>
      </div>
    </div>,
    document.body,
  );
}
